/**
 * ImportHistorySection — past import batches with archive / restore.
 *
 * Archiving is the undo story for a bad import: it archives every
 * record the batch created (contacts/companies get archived_at, deals
 * a soft-delete tombstone) and is fully reversible with Restore.
 * Archive sits behind an AlertDialog confirm; both operations are
 * chunked server-side, so this component keeps calling until the
 * server reports remaining === 0.
 */

import { useState } from 'react';
import { Archive, ArchiveRestore } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import type { FiasLeadsImportBatchDto } from '@/lib/api';
import {
  useArchiveFiasLeadsImportBatch,
  useFiasLeadsImportBatches,
  useRestoreFiasLeadsImportBatch,
} from '../../hooks/useFiasLeadsImport';

const ENTITY_LABEL: Record<FiasLeadsImportBatchDto['entityType'], string> = {
  contact: 'Contacts',
  company: 'Companies',
  deal: 'Deals',
};

function statusBadge(status: FiasLeadsImportBatchDto['status']) {
  switch (status) {
    case 'archived':
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Archived
        </Badge>
      );
    case 'archiving':
      return (
        <Badge variant="outline" className="text-amber-600 dark:text-amber-400">
          Archiving…
        </Badge>
      );
    case 'restoring':
      return (
        <Badge variant="outline" className="text-amber-600 dark:text-amber-400">
          Restoring…
        </Badge>
      );
    case 'uploading':
      return (
        <Badge variant="outline" className="text-amber-600 dark:text-amber-400">
          Interrupted
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400">
          Imported
        </Badge>
      );
  }
}

interface Props {
  holderId: string;
}

export function ImportHistorySection({ holderId }: Props) {
  const { toast } = useToast();
  const { data: batches = [], isLoading } = useFiasLeadsImportBatches(holderId);
  const archiveMutation = useArchiveFiasLeadsImportBatch(holderId);
  const restoreMutation = useRestoreFiasLeadsImportBatch(holderId);

  const [confirmBatch, setConfirmBatch] = useState<FiasLeadsImportBatchDto | null>(null);
  const [busyBatchId, setBusyBatchId] = useState<string | null>(null);

  /** Drives a chunked lifecycle operation until the server reports done. */
  const runUntilDone = async (
    batch: FiasLeadsImportBatchDto,
    mutation: typeof archiveMutation,
    verb: 'archived' | 'restored',
  ) => {
    setBusyBatchId(batch.id);
    try {
      let remaining = Infinity;
      let failed = 0;
      while (remaining > 0) {
        const result = await mutation.mutateAsync({
          batchId: batch.id,
          entityType: batch.entityType,
        });
        remaining = result.remaining;
        failed += result.failed.length;
      }
      toast({
        title: `Import ${verb}`,
        description:
          failed > 0
            ? `Done, but ${failed} record${failed === 1 ? '' : 's'} could not be ${verb}.`
            : `${ENTITY_LABEL[batch.entityType]} from "${batch.fileName || 'this import'}" ${verb}.`,
        ...(failed > 0 ? { variant: 'destructive' as const } : {}),
      });
    } catch (err) {
      toast({
        title: `Could not complete — try again`,
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBusyBatchId(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading import history…</p>;
  }

  if (batches.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No imports yet. Every CSV import you run shows up here, and can be archived (undone) as a
        unit if something went wrong.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Archive an import to hide everything it created — restore brings it all back.
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Imported</TableHead>
              <TableHead className="text-right">Skipped</TableHead>
              <TableHead className="text-right">Failed</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((batch) => {
              const busy = busyBatchId === batch.id;
              const archivable =
                (batch.status === 'active' ||
                  batch.status === 'uploading' ||
                  batch.status === 'archiving') &&
                !busy;
              const restorable =
                (batch.status === 'archived' || batch.status === 'restoring') && !busy;
              return (
                <TableRow key={batch.id}>
                  <TableCell className="text-sm font-medium">
                    {batch.fileName || 'Untitled import'}
                  </TableCell>
                  <TableCell className="text-sm">{ENTITY_LABEL[batch.entityType]}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(batch.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right text-sm">{batch.createdCount}</TableCell>
                  <TableCell className="text-right text-sm">{batch.skippedCount}</TableCell>
                  <TableCell className="text-right text-sm">{batch.failedCount}</TableCell>
                  <TableCell>
                    {busy
                      ? statusBadge(
                          batch.status === 'archived' || batch.status === 'restoring'
                            ? 'restoring'
                            : 'archiving',
                        )
                      : statusBadge(batch.status)}
                  </TableCell>
                  <TableCell className="text-right">
                    {archivable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmBatch(batch)}
                        aria-label={`Archive import ${batch.fileName}`}
                      >
                        <Archive className="mr-1 h-3.5 w-3.5" />
                        Archive
                      </Button>
                    )}
                    {restorable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void runUntilDone(batch, restoreMutation, 'restored')}
                        aria-label={`Restore import ${batch.fileName}`}
                      >
                        <ArchiveRestore className="mr-1 h-3.5 w-3.5" />
                        Restore
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirmBatch} onOpenChange={(open) => !open && setConfirmBatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this import?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmBatch && (
                <>
                  This hides all {confirmBatch.createdCount}{' '}
                  {ENTITY_LABEL[confirmBatch.entityType].toLowerCase()} imported from "
                  {confirmBatch.fileName || 'this import'}". Records you archived or changed
                  yourself are left alone. You can restore the import at any time.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const batch = confirmBatch;
                setConfirmBatch(null);
                if (batch) void runUntilDone(batch, archiveMutation, 'archived');
              }}
            >
              Archive import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
